import { ABaseModel } from '../abstracts/ABaseModel.js';
import {
  IArt,
  IArtLocale,
  IArtPaginationResult,
  IArtFlat,
  IArtAction,
  IArtFlatPaginationResult,
  IArtFlatListResult,
  IArtMeta,
  IArtMedia,
  IArtMediaPaginationResult,
  IArtRanking,
  IProductSku,
  IProductSkuLocale,
  IProductStore,
  IProductSkuStore,
  IProductSkuOption,
  IProductSkuOptionValue,
  IProductEditorial,
  IProductEditorialLocale,
  IProductEditorialPaginationResult,
} from '../interfaces/Art.js';
import { Image, ImageHierarchical } from './Image.js';
import { PaginationResult, ListResult } from './List.js';
import { Author } from './User.js';
import { Artist } from './Artist.js';

/**
 * ArtFlat
 */
class ArtFlat extends ABaseModel implements IArtFlat {
  artistId: number;
  artistAlias: string;
  artistName: string;
  canBuy: boolean;
  canRent: boolean;
  collectionDescription: string;
  collectionId: number;
  depth: number;
  height: number;
  id: number;
  images: Array<Image>;
  isActive: boolean;
  isEnquire: boolean;
  isProduct: boolean;
  isRented: boolean;
  isSoldOut: boolean;
  is3D: boolean;
  medium: string;
  style: string;
  subject: string;
  metaSizeValue: string;
  productId: number;
  productStatus: number;
  productType: string;
  skuBuyId: number;
  skuBuyPrice: number;
  skuBuyStatus: number;
  skuBuyStock: number;
  skuPrimaryEditionSize: number;
  skuPrimaryId: number;
  skuPrimaryStatus: number;
  skuPrimarySellerId: number;
  skuPrimarySellerIntegrationId: number;
  skuPrimarySellerName: string;
  skuPrimarySellerType: number;
  skuPrimarySellerCountry: string;
  skuRentId: number;
  skuRentInventory: number;
  skuRentPrice: number;
  skuRentStatus: number;
  skuRentStock: number;
  status: number;
  storeCode: string;
  title: string;
  unit: string;
  slug: string;
  width: number;

  /**
   * constructor
   */
  constructor() {
    super();
  }
}

/**
 * ArtFlatPaginationResult
 */
class ArtFlatPaginationResult extends PaginationResult implements IArtFlatPaginationResult {
  declare items: Array<ArtFlat>;

  /**
   * constructor
   */
  constructor() {
    super();
  }
}

/**
 * ArtFlatListResult
 */
class ArtFlatListResult extends ListResult implements IArtFlatListResult {
  declare items: Array<ArtFlat>;

  /**
   * constructor
   */
  constructor() {
    super();
  }
}

/**
 * ArtLocale
 */
class ArtLocale extends ABaseModel implements IArtLocale {
  id: number;
  localeCode: string;
  slug: string;
  materials: string;
  description: string;
  statusMessage: string;

  /**
   * constructor
   */
  constructor() {
    super();
  }
}

/**
 * ArtAction
 */
class ArtAction extends ABaseModel implements IArtAction {
  id: string;
  action: string;
  artId: number;
  executed: string;
  status: string;

  /**
   * constructor
   */
  constructor() {
    super();
  }
}

/**
 * ArtMeta
 */
class ArtMeta extends ABaseModel implements IArtMeta {
  id: number;
  artId: number;
  type: string;
  value: string;

  /**
   * constructor
   */
  constructor() {
    super();
  }
}

/**
 * ArtMedia
 */
class ArtMedia extends ABaseModel implements IArtMedia {
  id: number;
  artId: number;
  status: number;
  mediaType: string;
  mediaPlatform: string;
  mediaId: string;

  /**
   * constructor
   */
  constructor() {
    super();
  }
}

/**
 * ArtMediaPaginationResult
 */
class ArtMediaPaginationResult extends PaginationResult implements IArtMediaPaginationResult {
  declare items: Array<ArtMedia>;

  /**
   * constructor
   */
  constructor() {
    super();
  }
}

/**
 * ArtRanking
 */
class ArtRanking extends ABaseModel implements IArtRanking {
  regionCode: string;
  rankingDefault: number;
  rankingRise: number;
  rankingVote: number;
  rankingAlltime: number;

  /**
   * constructor
   */
  constructor() {
    super();
  }
}

/**
 * Art
 */
class Art extends ABaseModel implements IArt {
  id: number;
  artist: Artist;
  depth: number;
  description: string;
  height: number;
  images: Array<ImageHierarchical>;
  materials: string;
  medium: string;
  orientation: string;
  state: string;
  status: number;
  statusMessage: string;
  style: string;
  subMedium: string;
  subSubject: string;
  subject: string;
  title: string;
  units: string;
  slug: string;
  width: number;
  year: number;
  bump: number;
  showDescription: boolean;
  isProduct: boolean;
  is3D: boolean;
  size: string;
  colors: Array<string>;
  tags: Array<string>;
  locales: Array<ArtLocale>;
  productId: number;
  productCanBuy: boolean;
  productCanRent: boolean;
  productCanActivate: boolean;
  productCanApprove: boolean;
  productCanDisapprove: boolean;
  productCanDelete: boolean;
  productCanEnquire: boolean;
  productCanUnlist: boolean;
  productCanSubmit: boolean;
  productCanSellOut: boolean;
  productCanArchive: boolean;
  productCanOffer: boolean;
  productInStore: boolean;
  productInStores: Array<string>;
  productIsEnquire: boolean;
  productIsRental: boolean;
  productIsRented: boolean;
  productIsSoldOut: boolean;
  productIsUnlisted: boolean;
  productIsPendingApproval: boolean;
  productIsActive: boolean;
  productIsArchived: boolean;
  productIsDeleted: boolean;
  productPrimarySku: ProductSku;
  productPublished: string;
  productSkus: Array<ProductSku>;
  productStores: Array<ProductStore>;
  productType: string;
  productShowcase: boolean;
  rankings: Array<ArtRanking>;

  /**
   * constructor
   */
  constructor() {
    super();
  }
}

/**
 * ArtPaginationResult
 */
class ArtPaginationResult extends PaginationResult implements IArtPaginationResult {
  declare items: Array<Art>;

  /**
   * constructor
   */
  constructor() {
    super();
  }
}

/**
 * ProductSku
 */
class ProductSku extends ABaseModel implements IProductSku {
  id: number;
  type: string;
  sku: string;
  stock: number;
  substrate: string;
  editionSize: number;
  width: number;
  height: number;
  depth: number;
  units: string;
  stores: Array<ProductSkuStore>;
  locales: Array<ProductSkuLocale>;
  framed: boolean;
  frameDescription: string;
  inStock: boolean;
  options: Array<ProductSkuOption>;
  physicalSku: string;
  unstretched: boolean;
  readyToHang: boolean;
  rental: boolean;
  activeRentals: number;
  signed: boolean;
  signedLocation: string;
  weight: number;
  year: number;
  shipsBy: string;
  shipsFramedBy: string;
  shipsFromCode: string;
  isActive: boolean;
  isSoldOut: boolean;
  isRented: boolean;
  canBuy: boolean;
  canRent: boolean;
  canBeFramed: boolean;
  canBuyInInstalments: boolean;
  canOffer: boolean;
  packageMethod: string;
  sellerId: number;
  sellerIntegrationId: number;
  sellerName: string;
  sellerType: number;
  sellerStore: string;
  sellerCurrency: string;
  sellerShippingTableId: number;
  sellerCanRentArt: boolean;
  sellerCanUseShippingTables: boolean;
  sellerOffers: boolean;
  sellerOfferMaximumDiscount: number;

  /**
   * constructor
   */
  constructor() {
    super();
  }
}

/**
 * ProductSkuLocale
 */
class ProductSkuLocale extends ABaseModel implements IProductSkuLocale {
  id: number;
  localeCode: string;
  frameDescription: string;
  signedLocation: string;

  /**
   * constructor
   */
  constructor() {
    super();
  }
}

/**
 * ProductStore
 */
class ProductStore extends ABaseModel implements IProductStore {
  id: number;
  status: number;
  storeCode: string;

  /**
   * constructor
   */
  constructor() {
    super();
  }
}

/**
 * ProductSkuStore
 */
class ProductSkuStore extends ABaseModel implements IProductSkuStore {
  id: number;
  status: number;
  storeCode: string;
  currency: string;
  price: number;
  priceInInstalments: number;

  /**
   * constructor
   */
  constructor() {
    super();
  }
}

/**
 * ProductSkuOption
 */
class ProductSkuOption extends ABaseModel implements IProductSkuOption {
  id: number;
  type: string;
  group: string;
  required: boolean;
  singleValue: boolean;
  inputType: string;
  isFrame: boolean;
  isInsurance: boolean;
  storeCode: string;
  title: string;
  values: Array<ProductSkuOptionValue>;

  /**
   * constructor
   */
  constructor() {
    super();
  }
}

/**
 * ProductSkuOptionValue
 */
class ProductSkuOptionValue extends ABaseModel implements IProductSkuOptionValue {
  id: number;
  optionId: number;
  storeCode: string;
  currency: string;
  price: number;
  title: string;

  /**
   * constructor
   */
  constructor() {
    super();
  }
}

/**
 * ProductEditorialLocale
 */
class ProductEditorialLocale extends ABaseModel implements IProductEditorialLocale {
  id: number;
  localeCode: string;
  value: any;

  /**
   * constructor
   */
  constructor() {
    super();
  }
}

/**
 * ProductEditorial
 */
class ProductEditorial extends ABaseModel implements IProductEditorial {
  id: number;
  productId: number;
  type: string;
  localizable: boolean;
  value: any;
  author: Author;
  locales: Array<ProductEditorialLocale>;
  images: Array<Image>;

  /**
   * constructor
   */
  constructor() {
    super();
  }
}

/**
 * ProductEditorialPaginationResult
 */
class ProductEditorialPaginationResult extends PaginationResult implements IProductEditorialPaginationResult {
  declare items: Array<ProductEditorial>;

  /**
   * constructor
   */
  constructor() {
    super();
  }
}

export {
  ArtFlat,
  ArtFlatPaginationResult,
  ArtFlatListResult,
  Art,
  ArtLocale,
  ArtAction,
  ArtMeta,
  ArtMedia,
  ArtMediaPaginationResult,
  ArtRanking,
  ArtPaginationResult,
  ProductSku,
  ProductSkuLocale,
  ProductStore,
  ProductSkuStore,
  ProductSkuOption,
  ProductSkuOptionValue,
  ProductEditorial,
  ProductEditorialLocale,
  ProductEditorialPaginationResult,
};
